import AudienceInsights from './AudienceInsights';
import { getReportMetricKind } from '../shared/report-table-styles';
import ReportLead from '../shared/ReportLead';
import { useEffect, useState, useContext, useCallback, useRef, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    useTheme,
    LinearProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
    Stack,
    Skeleton,
    Tabs,
    Tab,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    useMediaQuery,
    ThemeProvider
} from '@mui/material';
import { AppVarsContext } from '../../context/AppVarsContext';
import ReactApexChart from 'react-apexcharts';
import { analyticsColors, reportColors, createReportSurfaceTheme, getReportSurfaceSx, getAnalyticsReportTabsSx, styleReportChart } from '../shared/report-design';
import GA4Pulse from './GA4Pulse';
import { formatUsd } from '../../system/report-formatters';
import LocationMap from '../../system/geo/LocationMap';
import {
    AcquisitionInsightVisuals,
    ConversionInsightVisuals,
    EcommerceInsightVisuals,
} from '../../system/ga4-insights';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatCurrency = (value) => formatUsd(Number(value || 0));
const normalizePercent = (value) => {
    const num = Number(value || 0);
    return num <= 1 ? num * 100 : num;
};
const formatPercent = (value, digits = 1) => `${normalizePercent(value).toFixed(digits)}%`;

const formatDuration = (value) => {
    if (value == null || value === '') return '0s';
    if (typeof value === 'string' && value.includes(':')) return value;
    const total = Number(value || 0);
    if (!Number.isFinite(total)) return '0s';
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = Math.round(total % 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const toLabel = (value, fallback = '(not set)') => {
    if (value == null) return fallback;
    const str = String(value).trim();
    return str.length ? str : fallback;
};

const pickNumber = (row, keys) => {
    for (const key of keys) {
        if (row && row[key] != null) return Number(row[key] || 0);
    }
    return 0;
};

const pickLabel = (row, keys) => {
    for (const key of keys) {
        if (row && row[key] != null && String(row[key]).trim()) return String(row[key]);
    }
    return '(not set)';
};

const buildTrend = (trend, fallbackKeys = []) => {
    if (!trend) return { categories: [], series: [] };
    if (trend.categories && trend.series) {
        return {
            categories: trend.categories || [],
            series: Array.isArray(trend.series) ? trend.series : []
        };
    }
    if (Array.isArray(trend)) {
        const categories = trend.map((row) => row.date || row.label || row.day || row.week || row.month || '');
        const keys = fallbackKeys.length
            ? fallbackKeys
            : Object.keys(trend[0] || {}).filter((key) => !['date', 'label', 'day', 'week', 'month'].includes(key));
        const series = keys.map((item) => {
            const key = typeof item === 'string' ? item : item.key;
            const name = typeof item === 'string' ? item : item.name || item.key;
            return {
                name,
                data: trend.map((row) => Number(row?.[key] || 0))
            };
        });
        return { categories, series };
    }
    return { categories: [], series: [] };
};

const EmptyState = ({ message }) => (
    <Box py={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
);

const ChartSkeleton = ({ height = 300 }) => (
    <Skeleton variant="rectangular" height={height} />
);

export const ResponsiveAnalyticsTable = ({ rows = [], columns = [], emptyMessage, rowKey }) => {
    const theme = useTheme();
    const useResponsiveRows = useMediaQuery(theme.breakpoints.down('md'));

    if (!rows.length) return <EmptyState message={emptyMessage} />;

    if (useResponsiveRows) {
        return (
            <Box role="list" sx={{ display: 'grid' }}>
                {rows.map((row, rowIndex) => (
                    <Box component="article" role="listitem" key={rowKey ? rowKey(row, rowIndex) : rowIndex} sx={{ py: 1.4, borderBottom: rowIndex === rows.length - 1 ? 0 : '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.1 }}>
                            {columns.map((column, columnIndex) => (
                                <Box data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} sx={{ minWidth: 0, gridColumn: columnIndex === 0 ? '1 / -1' : 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.25 }}>{column.label}</Typography>
                                    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere', fontSize: '0.88rem', fontWeight: columnIndex === 0 ? 700 : 500 }}>
                                        {column.render ? column.render(row[column.key], row) : row[column.key]}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((column) => <TableCell data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} align={column.align || 'left'}><strong>{column.label}</strong></TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowKey ? rowKey(row, rowIndex) : rowIndex}>
                            {columns.map((column) => (
                                <TableCell data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)} key={column.key} align={column.align || 'left'}>
                                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

function TabPanel({ value, index, children }) {
    if (value !== index) return null;
    return (
        <Box role="tabpanel" id={`ga4-panel-${index}`} aria-labelledby={`ga4-tab-${index}`} sx={{ minWidth: 0 }}>
            {children}
        </Box>
    );
}

export default function GA4SummaryView(props) {
    const cid = sessionStorage.getItem('rid');
    const [sdate] = useState(sessionStorage.getItem('sDate') || '');
    const [edate] = useState(sessionStorage.getItem('eDate') || '');
    const reportProfile = props.reportProfile;
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [report, setReport] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const { appVars } = useContext(AppVarsContext);
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const reportRef = useRef();
    const fetchIdRef = useRef(0);
    const dateLabel = [sessionStorage.getItem('sDate'), sessionStorage.getItem('eDate')]
        .filter(Boolean).map((date) => new Date(`${date}T00:00:00Z`))
        .filter((date) => Number.isFinite(date.getTime()))
        .map((date) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date)).join(' – ');
    const isSkeleton = loading && !report;
    const sectionCardSx = getReportSurfaceSx(theme);
    const tableSectionSx = sectionCardSx;
    const metricGridSx = (layout) => ({ ...layout, '& .MuiCard-root': sectionCardSx });
    const pageShellSx = {
        p: { xs: 1.5, md: 3 }, bgcolor: theme.palette.background.default, color: theme.palette.text.primary,
        minWidth: 0, display: 'grid', gap: 2,
        '& .MuiCardHeader-root': { p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` },
        '& .MuiCardHeader-title': { fontSize: '1rem', fontWeight: 700 },
        '& .MuiCardHeader-subheader': { fontSize: '0.875rem', color: theme.palette.text.secondary, mt: 0.5 },
        '& .MuiCardContent-root': { p: { xs: 1.5, md: 2.5 } },
        '& .MuiTableCell-root': { px: 1.5, py: 1.25, borderColor: theme.palette.divider, fontVariantNumeric: 'tabular-nums' },
        '& .MuiTableCell-head': { bgcolor: theme.palette.background.default, color: theme.palette.text.primary },
        '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': { bgcolor: theme.palette.background.default },
        '@media print': { p: 0, bgcolor: 'white' }
    };
    const chartColors = reportColors.categories;

    const baseChartOptions = useMemo(() => ({
        chart: {
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: theme.typography.fontFamily
        },
        dataLabels: { enabled: false },
        grid: { borderColor: theme.palette.divider },
        colors: chartColors,
        tooltip: { theme: theme.palette.mode }
    }), [theme, chartColors]);

    const formatChartValue = useCallback((value, format = 'number', digits = 2) => {
        switch (format) {
            case 'currency':
                return formatCurrency(value);
            case 'percent':
                return formatPercent(value, digits);
            case 'duration':
                return formatDuration(value);
            default:
                return formatNumber(value);
        }
    }, []);

    const lineOptions = useCallback((categories, config = {}) => {
        const {
            yAxisFormat = 'number',
            tooltipFormats = [],
            yaxis: yaxisOverride
        } = config;

        return styleReportChart(theme, {
            ...baseChartOptions,
            chart: { ...baseChartOptions.chart, type: 'line' },
            stroke: { curve: 'smooth', width: 3 },
            xaxis: { categories, labels: { style: { colors: theme.palette.text.secondary } } },
            yaxis: yaxisOverride || {
                labels: {
                    style: { colors: theme.palette.text.secondary },
                    formatter: (value) => formatChartValue(value, yAxisFormat)
                }
            },
            tooltip: {
                ...baseChartOptions.tooltip,
                y: {
                    formatter: (value, context) => {
                        const nextFormat = tooltipFormats[context?.seriesIndex] || yAxisFormat;
                        return formatChartValue(value, nextFormat);
                    }
                }
            },
            legend: { position: 'bottom' }
        });
    }, [baseChartOptions, formatChartValue, theme]);

    const barOptions = useCallback((categories, config = {}) => {
        const { valueFormat = 'number' } = config;
        return styleReportChart(theme, {
            ...baseChartOptions,
            chart: { ...baseChartOptions.chart, type: 'bar' },
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '65%' } },
            xaxis: {
                categories,
                labels: {
                    style: { colors: theme.palette.text.secondary },
                    formatter: (value) => formatChartValue(value, valueFormat)
                }
            },
            yaxis: { labels: { style: { colors: theme.palette.text.secondary } } },
            tooltip: {
                ...baseChartOptions.tooltip,
                y: {
                    formatter: (value) => formatChartValue(value, valueFormat)
                }
            }
        });
    }, [baseChartOptions, formatChartValue, theme]);

    const mapLegacyToReport = useCallback((summary, trafficRows) => {
        const conversions = Number(summary?.goals || 0);
        return {
            summary: {
                kpis: {
                    users: Number(summary?.visitors || 0),
                    newUsers: Number(summary?.newVisitors || 0),
                    sessions: Number(summary?.sessions || summary?.visitors || 0),
                    engagementRate: Number(summary?.engagementRate || 0),
                    avgEngagementTime: summary?.avgTimeFormatted || summary?.avgTime || 0,
                    conversions
                },
                trend: summary?.trend || summary?.trendByDate || []
            },
            traffic: {
                sources: (trafficRows || []).map((row) => ({
                    source: row?.source,
                    medium: row?.medium,
                    sourceMedium: row?.sourceMedium,
                    sessions: Number(row?.sessions || 0),
                    users: Number(row?.users || row?.sessions || 0),
                    newUsers: Number(row?.newUsers || 0),
                    engagementRate: Number(row?.engagementRate || 0),
                    avgEngagementTime: row?.avgEngagementTime || 0,
                    conversions: Number(row?.purchases || row?.conversions || 0),
                    revenue: Number(row?.revenue || 0)
                }))
            },
            conversions: {
                summary: {
                    conversions,
                    conversionRate: Number(summary?.conversionRate || 0),
                    users: Number(summary?.visitors || 0),
                    revenue: Number(summary?.revenue || 0)
                },
                events: (summary?.goalsByName || []).map((row) => ({
                    name: row?.name,
                    conversions: Number(row?.count || 0),
                    users: Number(row?.users || 0),
                    conversionRate: Number(row?.rate || 0),
                    revenue: Number(row?.revenue || 0)
                }))
            }
        };
    }, []);

    const fetchReport = async () => {
        const fetchId = ++fetchIdRef.current;
        const requestDates = { sdate: sessionStorage.getItem('sDate'), edate: sessionStorage.getItem('eDate') };

        if (props.setLoading) {
            props.setLoading(true);
        }
        setErr('');
        setLoading(true);
        let fallbackUsed = false;
        try {
            const res = await fetch(`${appVars.api_url}/google/analytics/ga4/report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid,
                    ...requestDates
                })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json?.detail || json?.error || 'Request failed');
            if (fetchId === fetchIdRef.current) setReport(json);
        } catch (primaryError) {
            if (fetchId !== fetchIdRef.current) return;
            try {
                const res = await fetch(`${appVars.api_url}/google/analytics/summary`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cid,
                        ...requestDates
                    })
                });
                const json = await res.json();
                if (!res.ok) throw new Error(json?.detail || json?.error || 'Request failed');
                const res2 = await fetch(`${appVars.api_url}/google/analytics/traffic`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cid,
                        ...requestDates,
                        limt: 10
                    })
                });
                const json2 = await res2.json();
                if (!res2.ok) throw new Error(json2?.detail || json2?.error || 'Request failed');
                if (fetchId !== fetchIdRef.current) return;
                setReport(mapLegacyToReport(json, json2?.rows || []));
                fallbackUsed = true;
            } catch (fallbackError) {
                if (fetchId === fetchIdRef.current) {
                    setErr(primaryError?.message || fallbackError?.message || String(fallbackError));
                    setReport(null);
                }
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                if (fallbackUsed) setErr('');
                setLoading(false);
                props?.setLoading?.(false);
            }
        }
    };

    const latestFetchReport = useRef(fetchReport);
    latestFetchReport.current = fetchReport;

    useEffect(() => {
        if (appVars?.api_url && cid && sdate && edate) latestFetchReport.current();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appVars?.api_url, cid, sdate, edate]);

    useEffect(() => {
        const requests = fetchIdRef;
        const handleStorageChange = () => { latestFetchReport.current(); };
        window.addEventListener('dc', handleStorageChange);
        return () => { ++requests.current; window.removeEventListener('dc', handleStorageChange); };
    }, []);

    const summaryKpis = report?.summary?.kpis || {};
    const summaryTrend = useMemo(() => buildTrend(report?.summary?.trend, [
        { key: 'users', name: 'Users' },
        { key: 'sessions', name: 'Sessions' }
    ]), [report]);

    const demographics = report?.demographics || {};
    const countryRows = useMemo(() => demographics.topCountries || [], [demographics.topCountries]);
    const locationRows = demographics.locations || countryRows;

    const traffic = report?.traffic || {};
    const trafficSources = useMemo(() => traffic.sources || [], [traffic.sources]);
    const sourceTrend = useMemo(() => buildTrend(traffic.sourceTrend, [
        { key: 'direct', name: 'Direct' },
        { key: 'organic', name: 'Organic' },
        { key: 'referral', name: 'Referral' },
        { key: 'social', name: 'Social' }
    ]), [traffic.sourceTrend]);
    const referrers = traffic.referrers || [];
    const referrerTrend = useMemo(() => buildTrend(traffic.referrerTrend), [traffic.referrerTrend]);
    const landingPages = traffic.landingPages || [];
    const landingTrend = useMemo(() => buildTrend(traffic.landingTrend), [traffic.landingTrend]);

    const conversions = report?.conversions || {};
    const conversionSummary = conversions.summary || {};
    const conversionEvents = useMemo(() => conversions.events || [], [conversions.events]);
    const conversionTrend = useMemo(() => buildTrend(conversions.trend), [conversions.trend]);
    const conversionPages = conversions.pages || [];
    const conversionPagesTrend = useMemo(() => buildTrend(conversions.pagesTrend), [conversions.pagesTrend]);

    const ecommerce = report?.ecommerce || {};
    const ecommerceKpis = ecommerce.kpis || {};
    const topProducts = useMemo(() => ecommerce.topProducts || [], [ecommerce.topProducts]);
    const topCategories = useMemo(() => ecommerce.topCategories || [], [ecommerce.topCategories]);
    const ecommerceTrend = useMemo(() => buildTrend(ecommerce.trend, [
        { key: 'revenue', name: 'Revenue' },
        { key: 'transactions', name: 'Transactions' }
    ]), [ecommerce.trend]);

    const buildSingleSeries = (rows, valueKeys, labelKeys, name) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        const categories = safeRows.map((row) => toLabel(pickLabel(row, labelKeys)));
        const series = [{ name, data: safeRows.map((row) => pickNumber(row, valueKeys)) }];
        return { categories, series };
    };



    const topProductsBar = useMemo(() => buildSingleSeries(topProducts, ['revenue', 'purchases', 'value'], ['name', 'product', 'label'], 'Revenue'), [topProducts]);
    const topCategoriesBar = useMemo(() => buildSingleSeries(topCategories, ['revenue', 'purchases', 'value'], ['name', 'category', 'label'], 'Revenue'), [topCategories]);

    const overviewSources = useMemo(() => [...(Array.isArray(trafficSources) ? trafficSources : [])]
        .filter((row) => Number(row?.sessions || 0) > 0)
        .sort((a, b) => Number(b?.sessions || 0) - Number(a?.sessions || 0))
        .slice(0, 5), [trafficSources]);
    const overviewEvents = useMemo(() => [...(Array.isArray(conversionEvents) ? conversionEvents : [])]
        .filter((row) => Number(row?.conversions || 0) > 0)
        .sort((a, b) => Number(b?.conversions || 0) - Number(a?.conversions || 0))
        .slice(0, 5), [conversionEvents]);
    const ga4Tokens = { color: analyticsColors };
    const rankedBarOptions = useCallback((categories, color, formatter = formatNumber) => styleReportChart(theme, {
        ...barOptions(categories),
        colors: [color],
        plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '58%' } },
        dataLabels: { enabled: true, formatter, style: { fontSize: '11px', fontWeight: 800 } },
        legend: { show: false },
    }), [barOptions, theme]);

    const tabsConfig = useMemo(() => ([
        { key: 'summary', label: 'Overview' },
        { key: 'demographics', label: 'Audience' },
        { key: 'traffic', label: 'Acquisition' },
        { key: 'conversions', label: 'Key Events' },
        { key: 'ecommerce', label: 'Ecommerce' }
    ]), []);

    const safeTabValue = Math.min(tabIndex, tabsConfig.length - 1);

    const siteName = report?.client?.name || report?.site?.name || reportProfile?.name || 'Google Analytics 4';
    const siteUrl = report?.client?.siteUrl || report?.site?.url || report?.site?.mainUrl || '';
    const siteLabel = useMemo(() => {
        if (!siteUrl) return siteName;
        try { return new URL(siteUrl).hostname.replace(/^www\./, ''); }
        catch (_) { return siteUrl; }
    }, [siteName, siteUrl]);

    return (
        <ThemeProvider theme={theme}>
        <Box sx={pageShellSx} ref={reportRef}>
            <ReportQuestionAssistant reportType="ga4" reportTitle="Google Analytics 4" client={{ id: cid }}
                profile={reportProfile || props.reportProfile} contextPayload={{ cid }} ga4Audit={report?.auditContext || null} />
            <Box sx={{ ...sectionCardSx, p: 1 }}>
                {isMobile ? (
                    <FormControl size="small" fullWidth>
                        <InputLabel id="ga4-tab-label">Report Page</InputLabel>
                        <Select labelId="ga4-tab-label" label="Report Page" value={safeTabValue} onChange={(event) => setTabIndex(Number(event.target.value))}>
                            {tabsConfig.map((tab, index) => <MenuItem key={tab.key} value={index}>{tab.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                ) : (
                    <Tabs aria-label="Analytics report pages" value={safeTabValue} onChange={(_, value) => setTabIndex(value)} variant="scrollable" scrollButtons="auto"
                        sx={getAnalyticsReportTabsSx()}>
                        {tabsConfig.map((tab, index) => <Tab key={tab.key} id={`ga4-tab-${index}`} aria-controls={`ga4-panel-${index}`} label={tab.label} />)}
                    </Tabs>
                )}
            </Box>
            {tabIndex > 0 && <Card sx={getReportSurfaceSx(theme)}><CardContent><ReportLead eyebrow="Google Analytics 4" title={['Overview', 'Audience', 'Acquisition', 'Key Events', 'Ecommerce'][tabIndex]} description="Explore the detail behind your website activity for the selected period." /></CardContent></Card>}
            {loading ? <LinearProgress aria-label="Loading analytics report" /> : null}
            {err ? <Alert severity="error">{err}</Alert> : null}
            {!report && !loading && !err ? <Alert severity="info">No analytics data found for the selected range.</Alert> : null}
            {(report || isSkeleton) && safeTabValue === 0 ? <GA4Pulse dateLabel={dateLabel} siteLabel={siteLabel}
                kpis={summaryKpis} conversionSummary={conversionSummary} trafficSources={trafficSources} conversionEvents={conversionEvents}
                loading={isSkeleton} formatNumber={formatNumber} formatDuration={formatDuration} /> : null}
            <TabPanel value={safeTabValue} index={0}>
                <Stack spacing={2} sx={{ p: 0 }}>
                    <Card variant="outlined" sx={sectionCardSx}>
                        <CardHeader titleTypographyProps={{ component: 'h2' }} title="Audience and activity over time" subheader="Users and sessions share one scale so changes in visit frequency remain visible." />
                        <CardContent>
                            {isSkeleton ? (
                                <ChartSkeleton height={320} />
                            ) : summaryTrend.series.length ? (
                                <ReactApexChart
                                    options={lineOptions(summaryTrend.categories)}
                                    series={summaryTrend.series}
                                    type="line"
                                    height={320}
                                />
                            ) : (
                                <EmptyState message="No trend data available for the selected range." />
                            )}
                        </CardContent>
                    </Card>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Where sessions came from" subheader="Top acquisition sources ranked by session volume." />
                            <CardContent>
                                {isSkeleton ? <ChartSkeleton height={285} /> : overviewSources.length ? (
                                    <Box role="img" aria-label={`Top acquisition sources. ${overviewSources.map((row) => `${row.sourceMedium || `${toLabel(row.source)} / ${toLabel(row.medium)}`}: ${formatNumber(row.sessions)} sessions`).join(', ')}.`}>
                                        <ReactApexChart
                                            options={rankedBarOptions(
                                                overviewSources.map((row) => row.sourceMedium || `${toLabel(row.source)} / ${toLabel(row.medium)}`),
                                                ga4Tokens.color.acquisition,
                                            )}
                                            series={[{ name: 'Sessions', data: overviewSources.map((row) => Number(row.sessions || 0)) }]}
                                            type="bar"
                                            height={285}
                                        />
                                    </Box>
                                ) : <EmptyState message="No acquisition-source data is available for this range." />}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="What visitors did" subheader="Top key events ranked by recorded actions." />
                            <CardContent>
                                {isSkeleton ? <ChartSkeleton height={285} /> : overviewEvents.length ? (
                                    <Box role="img" aria-label={`Top key events. ${overviewEvents.map((row) => `${toLabel(row.name || row.event)}: ${formatNumber(row.conversions)}`).join(', ')}.`}>
                                        <ReactApexChart
                                            options={rankedBarOptions(
                                                overviewEvents.map((row) => toLabel(row.name || row.event)),
                                                ga4Tokens.color.outcome,
                                            )}
                                            series={[{ name: 'Key events', data: overviewEvents.map((row) => Number(row.conversions || 0)) }]}
                                            type="bar"
                                            height={285}
                                        />
                                    </Box>
                                ) : <EmptyState message="No key-event data is available for this range." />}
                            </CardContent>
                        </Card>
                    </Box>
                </Stack>
            </TabPanel>

            <TabPanel value={safeTabValue} index={1}>
                <Stack spacing={2}>
                    <AudienceInsights report={report} loading={isSkeleton} contentInset={{ xs: 1.5, md: 2.5 }} />
                    <Card variant="outlined">
                        <CardHeader title="Explore locations" subheader="Geographic detail" />
                        <CardContent><LocationMap rows={locationRows} loading={isSkeleton} /></CardContent>
                    </Card>
                </Stack>
            </TabPanel>

            <TabPanel value={safeTabValue} index={2}>
                <Stack spacing={2} sx={{ p: 0 }}>
                    <AcquisitionInsightVisuals sources={trafficSources} loading={isSkeleton} cardSx={sectionCardSx} />
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }
                        }}
                    >
                        <Card variant="outlined" sx={tableSectionSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Traffic Sources" subheader="Sessions by source / medium" />
                            <CardContent>
                                {isSkeleton ? (
                                    <Skeleton variant="rectangular" height={260} />
                                ) : (
                                    <ResponsiveAnalyticsTable
                                        rows={trafficSources}
                                        emptyMessage="No traffic sources found."
                                        rowKey={(row, index) => `${row.sourceMedium || row.source}-${index}`}
                                        columns={[
                                            { key: 'sourceMedium', label: 'Source / medium', render: (_, row) => row.sourceMedium || `${toLabel(row.source)} / ${toLabel(row.medium)}` },
                                            { key: 'sessions', label: 'Sessions', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'users', label: 'Users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'newUsers', label: 'New users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'engagementRate', label: 'Engagement rate', align: 'right', render: (value) => formatPercent(value, 1) },
                                            { key: 'conversions', label: 'Key events', align: 'right', render: (value) => formatNumber(value) },
                                        ]}
                                    />
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Traffic Trend" subheader="Sessions by channel" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={260} />
                                ) : sourceTrend.series.length ? (
                                    <ReactApexChart
                                        options={lineOptions(sourceTrend.categories)}
                                        series={sourceTrend.series}
                                        type="line"
                                        height={260}
                                    />
                                ) : (
                                    <EmptyState message="No traffic trend data available." />
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }
                        }}
                    >
                        <Card variant="outlined" sx={tableSectionSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Referrers" subheader="Sessions by referrer" />
                            <CardContent>
                                {isSkeleton ? (
                                    <Skeleton variant="rectangular" height={240} />
                                ) : (
                                    <ResponsiveAnalyticsTable
                                        rows={referrers}
                                        emptyMessage="No referrer data found."
                                        rowKey={(row, index) => `${row.referrer || row.name}-${index}`}
                                        columns={[
                                            { key: 'referrer', label: 'Referrer', render: (_, row) => toLabel(row.referrer || row.name) },
                                            { key: 'sessions', label: 'Sessions', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'users', label: 'Users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'newUsers', label: 'New users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'engagementRate', label: 'Engagement rate', align: 'right', render: (value) => formatPercent(value, 1) },
                                        ]}
                                    />
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Referrer Trend" subheader="Sessions over time" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={240} />
                                ) : referrerTrend.series.length ? (
                                    <ReactApexChart
                                        options={lineOptions(referrerTrend.categories)}
                                        series={referrerTrend.series}
                                        type="line"
                                        height={240}
                                    />
                                ) : (
                                    <EmptyState message="No referrer trend data available." />
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }
                        }}
                    >
                        <Card variant="outlined" sx={tableSectionSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Landing Pages" subheader="Sessions by landing page" />
                            <CardContent>
                                {isSkeleton ? (
                                    <Skeleton variant="rectangular" height={240} />
                                ) : (
                                    <ResponsiveAnalyticsTable
                                        rows={landingPages}
                                        emptyMessage="No landing page data found."
                                        rowKey={(row, index) => `${row.page || row.name}-${index}`}
                                        columns={[
                                            { key: 'page', label: 'Landing page', render: (_, row) => toLabel(row.page || row.name) },
                                            { key: 'sessions', label: 'Sessions', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'users', label: 'Users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'engagementRate', label: 'Engagement rate', align: 'right', render: (value) => formatPercent(value, 1) },
                                            { key: 'conversions', label: 'Key events', align: 'right', render: (value) => formatNumber(value) },
                                        ]}
                                    />
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Landing Page Trend" subheader="Sessions over time" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={240} />
                                ) : landingTrend.series.length ? (
                                    <ReactApexChart
                                        options={lineOptions(landingTrend.categories)}
                                        series={landingTrend.series}
                                        type="line"
                                        height={240}
                                    />
                                ) : (
                                    <EmptyState message="No landing page trend data available." />
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                </Stack>
            </TabPanel>

            <TabPanel value={safeTabValue} index={3}>
                <Stack spacing={2} sx={{ p: 0 }}>
                    <Card variant="outlined" sx={sectionCardSx}>
                        <CardHeader titleTypographyProps={{ component: 'h2' }} title="Conversions" subheader="Key conversion metrics" />
                        <CardContent>
                            <Box sx={metricGridSx({
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(4, minmax(0, 1fr))'
                                }
                            })}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Conversions</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatNumber(conversionSummary.conversions)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Conversion Rate</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatPercent(conversionSummary.conversionRate, 2)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Users</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatNumber(conversionSummary.users)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Revenue</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatCurrency(conversionSummary.revenue)}</Typography>}
                                    </CardContent>
                                </Card>
                            </Box>
                        </CardContent>
                    </Card>

                    <ConversionInsightVisuals events={conversionEvents} loading={isSkeleton} cardSx={sectionCardSx} />

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }
                        }}
                    >
                        <Card variant="outlined" sx={tableSectionSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Conversion Events" subheader="Conversions by event" />
                            <CardContent>
                                {isSkeleton ? (
                                    <Skeleton variant="rectangular" height={240} />
                                ) : (
                                    <ResponsiveAnalyticsTable
                                        rows={conversionEvents}
                                        emptyMessage="No key-event data found."
                                        rowKey={(row, index) => `${row.name || row.event}-${index}`}
                                        columns={[
                                            { key: 'name', label: 'Key event', render: (_, row) => toLabel(row.name || row.event) },
                                            { key: 'conversions', label: 'Count', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'users', label: 'Users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'conversionRate', label: 'Session action rate', align: 'right', render: (value) => formatPercent(value, 2) },
                                            { key: 'revenue', label: 'Revenue', align: 'right', render: (value) => formatCurrency(value) },
                                        ]}
                                    />
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Conversion Trend" subheader="Conversions over time" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={240} />
                                ) : conversionTrend.series.length ? (
                                    <ReactApexChart
                                        options={lineOptions(conversionTrend.categories)}
                                        series={conversionTrend.series}
                                        type="line"
                                        height={240}
                                    />
                                ) : (
                                    <EmptyState message="No conversion trend data available." />
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }
                        }}
                    >
                        <Card variant="outlined" sx={tableSectionSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Pages" subheader="Conversions by page" />
                            <CardContent>
                                {isSkeleton ? (
                                    <Skeleton variant="rectangular" height={220} />
                                ) : (
                                    <ResponsiveAnalyticsTable
                                        rows={conversionPages}
                                        emptyMessage="No key-event page data found."
                                        rowKey={(row, index) => `${row.page || row.name}-${index}`}
                                        columns={[
                                            { key: 'page', label: 'Page', render: (_, row) => toLabel(row.page || row.name) },
                                            { key: 'conversions', label: 'Key events', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'users', label: 'Users', align: 'right', render: (value) => formatNumber(value) },
                                            { key: 'conversionRate', label: 'Session action rate', align: 'right', render: (value) => formatPercent(value, 2) },
                                        ]}
                                    />
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Pages Trend" subheader="Conversions over time" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={220} />
                                ) : conversionPagesTrend.series.length ? (
                                    <ReactApexChart
                                        options={lineOptions(conversionPagesTrend.categories)}
                                        series={conversionPagesTrend.series}
                                        type="line"
                                        height={220}
                                    />
                                ) : (
                                    <EmptyState message="No conversion page trend data available." />
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                </Stack>
            </TabPanel>

            <TabPanel value={safeTabValue} index={4}>
                <Stack spacing={2} sx={{ p: 0 }}>
                    <Card variant="outlined" sx={sectionCardSx}>
                        <CardHeader titleTypographyProps={{ component: 'h2' }} title="Ecommerce" subheader="Revenue & order performance" />
                        <CardContent>
                            <Box sx={metricGridSx({
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                    lg: 'repeat(6, minmax(0, 1fr))'
                                }
                            })}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Transactions</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatNumber(ecommerceKpis.transactions)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Ecom Conv. Rate</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatPercent(ecommerceKpis.conversionRate, 2)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Revenue</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatCurrency(ecommerceKpis.revenue)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Avg Order Value</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatCurrency(ecommerceKpis.aov)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">Items / Purchase</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatNumber(ecommerceKpis.itemsPerPurchase)}</Typography>}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="overline" color="text.secondary">ARPU</Typography>
                                        {isSkeleton ? <Skeleton variant="text" height={32} /> : <Typography component="p" variant="h5" fontWeight={700}>{formatCurrency(ecommerceKpis.arpu)}</Typography>}
                                    </CardContent>
                                </Card>
                            </Box>
                        </CardContent>
                    </Card>

                    <EcommerceInsightVisuals products={topProducts} categories={topCategories} loading={isSkeleton} cardSx={sectionCardSx} />

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }
                        }}
                    >
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Products" subheader="Revenue by product" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={300} />
                                ) : topProductsBar.series[0]?.data?.length ? (
                                    <ReactApexChart
                                        options={barOptions(topProductsBar.categories, { valueFormat: 'currency' })}
                                        series={topProductsBar.series}
                                        type="bar"
                                        height={300}
                                    />
                                ) : (
                                    <EmptyState message="No ecommerce product data available." />
                                )}
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={sectionCardSx}>
                            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Top Categories" subheader="Revenue by category" />
                            <CardContent>
                                {isSkeleton ? (
                                    <ChartSkeleton height={300} />
                                ) : topCategoriesBar.series[0]?.data?.length ? (
                                    <ReactApexChart
                                        options={barOptions(topCategoriesBar.categories, { valueFormat: 'currency' })}
                                        series={topCategoriesBar.series}
                                        type="bar"
                                        height={300}
                                    />
                                ) : (
                                    <EmptyState message="No ecommerce category data available." />
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    <Card variant="outlined" sx={sectionCardSx}>
                        <CardHeader titleTypographyProps={{ component: 'h2' }} title="Ecommerce Trend" subheader="Revenue & transactions over time" />
                        <CardContent>
                            {isSkeleton ? (
                                <ChartSkeleton height={320} />
                            ) : ecommerceTrend.series.length ? (
                                <ReactApexChart
                                    options={lineOptions(ecommerceTrend.categories, {
                                        tooltipFormats: ['currency', 'number'],
                                        yaxis: [
                                            {
                                                seriesName: 'Revenue',
                                                labels: {
                                                    style: { colors: theme.palette.text.secondary },
                                                    formatter: (value) => formatCurrency(value)
                                                }
                                            },
                                            {
                                                seriesName: 'Transactions',
                                                opposite: true,
                                                labels: {
                                                    style: { colors: theme.palette.text.secondary },
                                                    formatter: (value) => formatNumber(value)
                                                }
                                            }
                                        ]
                                    })}
                                    series={ecommerceTrend.series}
                                    type="line"
                                    height={320}
                                />
                            ) : (
                                <EmptyState message="No ecommerce trend data available." />
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </TabPanel>
        </Box>
        </ThemeProvider>
    );
}
